"use client"

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { X, Plus, Copy, Circle, Mail, Settings2, Check, Search, AlertTriangle, Eye, Trash2, ChevronDown, ChevronRight, Bot, History } from "lucide-react"
import { useVmsApi } from "@/hooks/vms/use-vms-api"
import { useVmsConfig } from "@/hooks/vms/use-vms-config"
import { useTranslation } from "@/hooks/use-translation"
import type { VmsEndpointDto, VmsSettingType, CreateVmsSettingsDto, VmsClientDto, VmsTypeCode, VmsUserReportDto, SettingsAuditEntryDto } from "@/types/vms/vms-settings"
import { ALLOWED_LLM_MODELS, MAX_TOKENS_CAP, DEFAULT_MAX_TOKENS, VMS_TYPES, getSettingType } from "@/types/vms/vms-settings"
import type { PromptConfigDto } from "@/types/vms/vms-settings"
import type { UserReportDto } from "@/types/workflow"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useAuth } from "@/contexts/auth-context"
import { copyTextToClipboard } from "@/utils/clipboard"

// ════════════════════════════════════════════════════════════
//  DEFAULT ASSIGNEES EDITOR — reusable across create + detail
// ════════════════════════════════════════════════════════════

/**
 * Chip-style multi-select for default-assignees. Debounced user search via the
 * existing /searchUsers endpoint. Hands the parent the full
 * <c>VmsUserReportDto</c> list (profileId, fullName, email) — the backend
 * persists it verbatim onto the settings doc.
 *
 * The first chip is rendered as the "primary" one because it's the entry that
 * gets copied onto AssignedToUser when a request is created from this endpoint;
 * the rest are stored for future quick-pick UX. Order matters — drag-to-reorder
 * isn't wired yet, so removing and re-adding is the way to change the primary.
 */
function DefaultAssigneesEditor({
  value,
  onChange,
  searchUsers,
  t,
}: {
  value: VmsUserReportDto[]
  onChange: (next: VmsUserReportDto[]) => void
  searchUsers: (q: string) => Promise<UserReportDto[]>
  t: (key: string) => string
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<UserReportDto[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastQueryRef = useRef("")
  const searchUsersRef = useRef(searchUsers)
  searchUsersRef.current = searchUsers

  // Refs + computed coords for the fixed-positioned dropdown. The input is inside
  // a scroll container (the form body), and the bottom of that container is masked
  // by the Save/Create action bar. An absolute+top-full dropdown gets clipped /
  // covered. Fixed positioning relative to the viewport escapes the overflow
  // context; we recompute on scroll-anywhere and resize so the dropdown follows
  // the input around. Style is only set after we've measured, so on first render
  // the dropdown doesn't briefly flash at (0,0).
  const inputWrapperRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | null>(null)

  useEffect(() => {
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current) }
  }, [])

  // Re-compute the dropdown's fixed coordinates whenever it opens or the page
  // is scrolled/resized while it's open. Scroll listener uses capture so it
  // fires for any ancestor's scroll, not just window.
  useEffect(() => {
    if (!showDropdown) {
      setDropdownStyle(null)
      return
    }
    const compute = () => {
      const el = inputWrapperRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 100,
      })
    }
    compute()
    window.addEventListener("scroll", compute, true)
    window.addEventListener("resize", compute)
    return () => {
      window.removeEventListener("scroll", compute, true)
      window.removeEventListener("resize", compute)
    }
  }, [showDropdown])

  // Click-outside to close. The fixed dropdown is visually outside the input's
  // DOM subtree (well, still nested in the React tree, but spatially separate),
  // so a user clicking somewhere else expects it to dismiss. We special-case
  // clicks on the dropdown itself so item selection isn't preempted.
  useEffect(() => {
    if (!showDropdown) return
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (inputWrapperRef.current?.contains(target)) return
      if (dropdownRef.current?.contains(target)) return
      setShowDropdown(false)
    }
    document.addEventListener("mousedown", handleMouseDown)
    return () => document.removeEventListener("mousedown", handleMouseDown)
  }, [showDropdown])

  const handleSearch = (q: string) => {
    setQuery(q)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (q.length < 2) {
      setResults([])
      setShowDropdown(false)
      return
    }
    timeoutRef.current = setTimeout(async () => {
      if (q === lastQueryRef.current) return
      lastQueryRef.current = q
      setIsSearching(true)
      try {
        const found = await searchUsersRef.current(q)
        // Filter out users already selected — keeps the dropdown tidy.
        const selectedIds = new Set(value.map(v => v.profileId))
        const filtered = found.filter(u => !selectedIds.has(u.profileId))
        setResults(filtered)
        setShowDropdown(filtered.length > 0)
      } catch {
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, 400)
  }

  const handleAdd = (u: UserReportDto) => {
    onChange([...value, { profileId: u.profileId, fullName: u.fullName, email: u.email }])
    setQuery("")
    setResults([])
    setShowDropdown(false)
    lastQueryRef.current = ""
  }

  const handleRemove = (profileId: string) => {
    onChange(value.filter(v => v.profileId !== profileId))
  }

  return (
    <div>
      {/* Chip row */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((u, idx) => (
            <span
              key={u.profileId}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                idx === 0
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "bg-gray-100 text-gray-700 border border-gray-200"
              }`}
              title={idx === 0 ? t('vmsPrimaryAssignee') : u.email}
            >
              {u.fullName || u.email}
              <button
                onClick={() => handleRemove(u.profileId)}
                className="p-0.5 rounded hover:bg-black/10 transition-colors"
                aria-label={`Remove ${u.fullName || u.email}`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input (wrapper carries the ref the dropdown positions against) */}
      <div ref={inputWrapperRef}>
        <Input
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => {
            // Re-show dropdown on refocus if there are pending results — happens
            // when the user dismissed via click-outside then came back.
            if (results.length > 0) setShowDropdown(true)
          }}
          placeholder={t('vmsSearchUsers')}
          className="text-sm"
        />
      </div>
      {isSearching && (
        <p className="text-xs text-gray-500 mt-1">{t('searchingUsers')}</p>
      )}
      {showDropdown && dropdownStyle && (
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
        >
          {results.map((u) => (
            <button
              key={u.profileId}
              onClick={() => handleAdd(u)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
            >
              <span className="font-medium text-gray-800">{u.fullName || u.email}</span>
              {u.fullName && (
                <span className="text-xs text-gray-400 ml-2">{u.email}</span>
              )}
            </button>
          ))}
        </div>
      )}
      {value.length > 0 && (
        <p className="text-[11px] text-gray-400 mt-1">
          {t('vmsPrimaryAssigneeNote')}
        </p>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════
//  COLLAPSIBLE SECTION — generic accordion-style wrapper used
//  by both SettingDetail and CreateSettingForm to keep the
//  Main info + LLM Configuration areas from running floor-to-
//  ceiling. Expanded by default; persists open/closed entirely
//  in component state (no need to remember across renders for
//  this UX).
// ════════════════════════════════════════════════════════════

function CollapsibleSection({
  title,
  icon,
  defaultOpen = true,
  children,
}: {
  title: string
  icon?: React.ReactNode
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        {isOpen ? (
          <ChevronDown size={14} className="text-gray-500 shrink-0" />
        ) : (
          <ChevronRight size={14} className="text-gray-500 shrink-0" />
        )}
        {icon && <span className="text-gray-600 shrink-0">{icon}</span>}
        <span className="text-sm font-medium text-gray-700">{title}</span>
      </button>
      {isOpen && (
        <div className="px-4 py-4 bg-white space-y-4">
          {children}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════
//  AUDIT HISTORY — timeline list of create/update/version/delete
//  rows for a settings group. Lazy-loaded on first expand so the
//  panel render stays cheap for users who never open it.
// ════════════════════════════════════════════════════════════

/**
 * Maps an audit action code to a label + badge color for the timeline.
 * Kept inline so adding a new action elsewhere only touches one place.
 */
function describeAuditAction(action: string): { label: string; classes: string } {
  switch (action) {
    case "settings_created":  return { label: "Created",   classes: "bg-green-50 text-green-700 border-green-200" }
    case "settings_updated":  return { label: "Updated",   classes: "bg-blue-50 text-blue-700 border-blue-200" }
    case "settings_versioned":return { label: "Versioned", classes: "bg-purple-50 text-purple-700 border-purple-200" }
    case "settings_deleted":  return { label: "Deleted",   classes: "bg-red-50 text-red-700 border-red-200" }
    default:                  return { label: action,      classes: "bg-gray-100 text-gray-700 border-gray-200" }
  }
}

/** Renders a single audit value cell. Stringifies nulls, booleans, lists, etc. */
function formatAuditValue(value: unknown): string {
  if (value === null || value === undefined) return "—"
  if (typeof value === "boolean") return value ? "true" : "false"
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

function AuditHistorySection({
  settingsGroupId,
  fetchHistory,
  t,
}: {
  settingsGroupId: string
  fetchHistory: (settingsGroupId: string) => Promise<SettingsAuditEntryDto[]>
  t: (key: string) => string
}) {
  const [entries, setEntries] = useState<SettingsAuditEntryDto[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const fetchRef = useRef(fetchHistory)
  fetchRef.current = fetchHistory

  // Refetch when the selected group changes. Setting entries=null short-circuits
  // the rendered list to the "loading" branch on next render, which is exactly
  // what we want — no stale data flash from the previous selection.
  useEffect(() => {
    let cancelled = false
    setEntries(null)
    setIsLoading(true)
    fetchRef.current(settingsGroupId)
      .then((rows) => {
        if (!cancelled) setEntries(rows)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => { cancelled = true }
  }, [settingsGroupId])

  if (isLoading || entries === null) {
    return (
      <div className="flex items-center justify-center py-4 text-xs text-gray-400">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2" />
        {t('loadingHistory')}
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <p className="text-xs text-gray-400 py-2">{t('vmsNoAuditEntries')}</p>
    )
  }

  return (
    <div className="space-y-3">
      {entries.map((entry) => {
        const meta = describeAuditAction(entry.action)
        return (
          <div key={entry.id} className="border border-gray-200 rounded-md p-3 bg-white">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${meta.classes}`}>
                {meta.label}
              </span>
              <span className="text-[11px] text-gray-500">
                {new Date(entry.createdDateTime).toLocaleString()}
              </span>
              {entry.createdByUserName && (
                <span className="text-[11px] text-gray-400">· {entry.createdByUserName}</span>
              )}
            </div>
            {entry.comment && (
              <p className="text-xs text-gray-600 mt-1.5">{entry.comment}</p>
            )}
            {entry.differences.length > 0 && (
              <div className="mt-2 space-y-1">
                {entry.differences.map((diff, idx) => (
                  <div
                    key={`${entry.id}-${diff.fieldName}-${idx}`}
                    className="text-[11px] grid grid-cols-[auto_1fr] gap-x-2 items-baseline"
                  >
                    <span className="text-gray-500 font-medium whitespace-nowrap">
                      {diff.displayName || diff.fieldName}:
                    </span>
                    <span className="text-gray-700 break-words">
                      <span className="text-gray-400 line-through">{formatAuditValue(diff.oldValue)}</span>
                      <span className="mx-1 text-gray-300">→</span>
                      <span>{formatAuditValue(diff.newValue)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ════════════════════════════════════════════════════════════
//  LEFT PANEL — List of settings with search + type filter
// ════════════════════════════════════════════════════════════

function SettingsList({
  items,
  selectedId,
  onSelect,
  onAdd,
  onDelete,
  canDelete,
  canCreateTenant,
  canCreateClient,
  searchText,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  showActiveOnly,
  onShowActiveOnlyChange,
  totalCount,
  loadedCount,
  onLoadMore,
  isLoadingMore,
  t,
}: {
  items: VmsEndpointDto[]
  selectedId: string | null
  onSelect: (id: string) => void
  onAdd: (type: VmsSettingType) => void
  onDelete: (id: string) => void
  canDelete: (endpoint: VmsEndpointDto) => boolean
  /**
   * Whether the user can add new Tenant settings (admin-tier write).
   * When false the "Add Tenant" button is hidden.
   */
  canCreateTenant: boolean
  /**
   * Whether the user can add new Client settings. True when the user has
   * either the Client-level or Tenant-level write code. When both
   * canCreateTenant and canCreateClient are false the whole add strip is
   * suppressed so the list reads as pure view-only.
   */
  canCreateClient: boolean
  searchText: string
  onSearchChange: (v: string) => void
  typeFilter: "all" | VmsSettingType
  onTypeFilterChange: (v: "all" | VmsSettingType) => void
  showActiveOnly: boolean
  onShowActiveOnlyChange: (v: boolean) => void
  /** Total settings on the server. May exceed loadedCount. */
  totalCount: number
  /** How many we've already loaded (across pages). */
  loadedCount: number
  /** Fetch the next page and append. */
  onLoadMore: () => Promise<void>
  isLoadingMore: boolean
  t: (key: string) => string
}) {
  // Whether the "Load more" affordance should appear at the bottom. Filters
  // are client-side so we always show this when there's more on the server,
  // regardless of how many items the current search/filter is showing.
  const hasMore = loadedCount < totalCount

  // Per-row copy feedback. Tracking by id (rather than a Set of "currently
  // copied" ids) means only one row at a time shows the checkmark — copying a
  // second row implicitly cancels the first row's confirmation.
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    return () => { if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current) }
  }, [])
  const handleCopyEmail = async (e: React.MouseEvent, ep: VmsEndpointDto) => {
    e.stopPropagation()  // don't also select the row
    // Await the helper so the "copied" checkmark only flashes when the copy
    // actually succeeded — previously a swallowed Promise rejection in
    // non-secure Chrome contexts let the UI claim success while the clipboard
    // stayed empty.
    const ok = await copyTextToClipboard(ep.endpointEmail)
    if (!ok) return
    setCopiedId(ep.id)
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
    copyTimeoutRef.current = setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <div className="flex flex-col h-full border-r border-gray-200">
      {/* Header area: search + filters + create */}
      <div className="px-5 pt-4 pb-3 space-y-3 border-b border-gray-100 shrink-0">
        {/* Create buttons — Tenant vs Client are gated independently.
            Tenant button is admin-tier only (canCreateTenant). Client
            button accepts the broader code (canCreateClient). The whole
            strip suppresses when neither is granted so a pure read-only
            user sees the list with no create affordances. When only one
            of the two is granted, the remaining button takes the full
            row width via flex-1 — no awkward half-empty layout. */}
        {(canCreateTenant || canCreateClient) && (
          <div className="flex items-center gap-2">
            {canCreateTenant && (
              <Button
                variant="elliptical-outline"
                size="sm"
                className="flex-1 h-9"
                onClick={() => onAdd("tenant")}
              >
                <Plus size={14} className="mr-1" />
                {t('vmsTenant')}
              </Button>
            )}
            {canCreateClient && (
              <Button
                variant="elliptical-outline"
                size="sm"
                className="flex-1 h-9"
                onClick={() => onAdd("client")}
              >
                <Plus size={14} className="mr-1" />
                {t('client')}
              </Button>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <Input
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('vmsSearchPlaceholder')}
            className="pl-9 text-sm h-9"
          />
        </div>

        {/* Type filter chips + active toggle */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {(["all", "tenant", "client"] as const).map((filterType) => (
            <button
              key={filterType}
              onClick={() => onTypeFilterChange(filterType)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                typeFilter === filterType
                  ? "bg-gray-800 text-white"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
            >
              {filterType === "all" ? t('vmsAllTypes') : filterType === "tenant" ? t('vmsTenant') : t('client')}
            </button>
          ))}

          {/* Active-only toggle */}
          <button
            onClick={() => onShowActiveOnlyChange(!showActiveOnly)}
            className={`ml-auto flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              showActiveOnly
                ? "bg-green-100 text-green-700 border border-green-200"
                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
            }`}
          >
            <Eye size={12} />
            {t('vmsActiveOnly')}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="px-5 py-8 text-center text-xs text-gray-400">
            {t('vmsNoSettingsMatch')}
          </div>
        ) : (
          items.map((ep) => {
            const isSelected = ep.id === selectedId
            const type = getSettingType(ep)
            return (
              <div
                key={ep.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(ep.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    onSelect(ep.id)
                  }
                }}
                // <div role="button"> instead of <button> because the row contains a
                // nested delete <button>. Nested buttons are invalid HTML and the
                // inner click bubbled unpredictably across browsers.
                className={`group w-full text-left px-5 py-3 border-b border-gray-50 transition-colors cursor-pointer ${
                  isSelected ? "bg-blue-50" : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Circle
                    size={6}
                    className={`shrink-0 fill-current ${ep.isActive ? "text-green-500" : "text-gray-300"}`}
                  />
                  <span className="text-sm font-medium text-gray-800 truncate flex-1">
                    {ep.displayName}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                    type === "tenant"
                      ? "bg-blue-50 text-blue-600"
                      : "bg-amber-50 text-amber-600"
                  }`}>
                    {type === "tenant" ? t('vmsTenant') : t('client')}
                  </span>
                  {canDelete(ep) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(ep.id) }}
                      className="p-1 rounded hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500 shrink-0"
                      title={t('delete')}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
                {type === "client" && ep.client && (
                  <p className="text-xs text-gray-400 mt-0.5 ml-4 truncate">
                    {ep.client.name}
                    {ep.client.obCode && (
                      <span className="ml-1">({ep.client.obCode})</span>
                    )}
                  </p>
                )}
                <div className="flex items-center gap-1.5 mt-1 ml-4 min-w-0">
                  <Mail size={13} className="text-gray-400 shrink-0" />
                  {/* Copy button sits IMMEDIATELY after the email text rather than
                      floated to the right edge of the row. The row's right edge
                      has the delete button (in the top line) — keeping the copy
                      button there meant the two affordances stacked vertically
                      and misclicks were easy. Now they're spatially separated:
                      delete top-right, copy mid-left next to its email. */}
                  {/* Stop propagation on mousedown/click so the user can
                      click-and-drag to highlight the email without the row's
                      onClick selecting a different setting mid-drag. Tailwind's
                      `select-text` overrides any parent `select-none` and
                      flags the text as user-selectable so Ctrl+C is a
                      reliable fallback when the copy button itself can't
                      access the clipboard (e.g. non-secure HTTP context). */}
                  <span
                    className="text-sm text-gray-600 truncate font-mono min-w-0 select-text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {ep.endpointEmail}
                  </span>
                  <button
                    onClick={(e) => handleCopyEmail(e, ep)}
                    className="shrink-0 p-1 rounded hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600"
                    title={t('vmsRowCopyEmail')}
                    aria-label={t('vmsRowCopyEmail')}
                  >
                    {copiedId === ep.id ? (
                      <Check size={13} className="text-green-600" />
                    ) : (
                      <Copy size={13} />
                    )}
                  </button>
                </div>
              </div>
            )
          })
        )}

        {/* Pagination footer: "Showing X of Y" hint + Load more button.
            Filters are client-side so the hint reflects loaded-vs-total at
            the server level, not the filtered view; without that distinction
            a user searching within page 1 would think there's no more data. */}
        {totalCount > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50">
            <p className="text-[11px] text-gray-400 mb-2">
              {t('vmsLoadedHint')
                .replace('{loaded}', String(loadedCount))
                .replace('{total}', String(totalCount))}
            </p>
            {hasMore && (
              <Button
                variant="elliptical-outline"
                size="sm"
                onClick={onLoadMore}
                disabled={isLoadingMore}
                className="w-full text-xs h-8"
              >
                {isLoadingMore ? t('vmsLoadingMore') : t('loadMore')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
//  UNSAVED CHANGES CONFIRMATION DIALOG (inline)
// ════════════════════════════════════════════════════════════

function UnsavedChangesBar({
  onSave,
  onDiscard,
  isSaving,
  t,
}: {
  onSave: () => void
  onDiscard: () => void
  isSaving: boolean
  t: (key: string) => string
}) {
  return (
    <div className="flex items-center gap-3 px-6 py-2.5 bg-amber-50 border-b border-amber-200 shrink-0">
      <AlertTriangle size={14} className="text-amber-600 shrink-0" />
      <span className="text-xs text-amber-800 flex-1">{t('unsavedChanges')}</span>
      <Button variant="elliptical" size="sm" onClick={onSave} disabled={isSaving} className="text-xs h-7">
        {isSaving ? t('atsSyncSaving') : t('save')}
      </Button>
      <Button variant="elliptical-outline" size="sm" onClick={onDiscard} className="text-xs h-7">
        {t('vmsDiscard')}
      </Button>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
//  DELETE CONFIRMATION DIALOG
// ════════════════════════════════════════════════════════════

function DeleteConfirmDialog({
  settingName,
  onConfirm,
  onCancel,
  isDeleting,
  t,
}: {
  settingName: string
  onConfirm: () => void
  onCancel: () => void
  isDeleting: boolean
  t: (key: string) => string
}) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/30 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
        <div className="flex items-center gap-2 mb-3">
          <Trash2 size={18} className="text-red-500" />
          <h3 className="font-semibold text-gray-800">{t('vmsDeleteSetting')}</h3>
        </div>
        <p className="text-sm text-gray-600 mb-1">
          {t('vmsDeleteConfirm')} <strong>{settingName}</strong>?
        </p>
        <p className="text-xs text-gray-400 mb-4">
          {t('vmsDeleteEmailWarning')}
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="elliptical-outline" size="sm" onClick={onCancel} disabled={isDeleting}>
            {t('cancel')}
          </Button>
          <Button
            variant="elliptical"
            size="sm"
            onClick={onConfirm}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isDeleting ? t('vmsDeleting') : t('delete')}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
//  RIGHT PANEL — Detail view of selected setting
// ════════════════════════════════════════════════════════════

function SettingDetail({
  endpoint,
  onToggleActive,
  onUpdate,
  onDelete,
  isSaving,
  hasUnsavedChanges,
  onHasChanges,
  canDelete,
  canModify,
  searchUsers,
  fetchAuditHistory,
  globalDefaults,
  emailDomain,
  t,
}: {
  endpoint: VmsEndpointDto
  onToggleActive: (id: string, isActive: boolean) => void
  onUpdate: (id: string, changes: Record<string, unknown>) => Promise<void>
  onDelete: (id: string) => void
  isSaving: boolean
  hasUnsavedChanges: boolean
  onHasChanges: (v: boolean) => void
  canDelete: boolean
  /**
   * Whether the user is allowed to edit fields on this settings doc. When
   * false the detail view is read-only: inputs disabled, the Save / Discard
   * unsaved-changes bar doesn't render, and the Active toggle is locked.
   * Same gate as Add and Delete — a single tenant-admin code.
   */
  canModify: boolean
  searchUsers: (q: string) => Promise<UserReportDto[]>
  fetchAuditHistory: (settingsGroupId: string) => Promise<SettingsAuditEntryDto[]>
  globalDefaults: PromptConfigDto | null
  emailDomain: string | null
  t: (key: string) => string
}) {
  const [copied, setCopied] = useState(false)
  const type = getSettingType(endpoint)

  // Local edit state for Main info
  const [displayName, setDisplayName] = useState(endpoint.displayName)
  const [notes, setNotes] = useState(endpoint.notes ?? "")
  // VmsType "" sentinel = "no pin"; the dropdown trigger renders the placeholder.
  const [vmsType, setVmsType] = useState<VmsTypeCode | "">(endpoint.vmsType ?? "")
  const [defaultAssignees, setDefaultAssignees] = useState<VmsUserReportDto[]>(endpoint.defaultAssignees ?? [])
  // Public-domain bypass — when true, this endpoint accepts emails from public
  // mail providers (gmail / outlook / etc.) and auto-creates a request. Off by
  // default; honoured by VmsEmailProcessingService against direct setting OR
  // linked LLM-config setting (either flag true = bypass).
  const [skipPublicDomainFilter, setSkipPublicDomainFilter] = useState<boolean>(endpoint.skipPublicDomainFilter ?? false)

  // Local edit state for LLM Config (tenant settings only — client settings
  // inherit from a linked tenant config, so the LLM fields aren't editable
  // here). Hoisted alongside main fields so a single handleSave can bundle
  // both into one update — that produces one version bump per save, and
  // matches the data model where main info + LLM live on the same doc.
  const llmInitial = (): LlmConfigValue => ({
    model: endpoint.llmModel ?? globalDefaults?.model ?? "claude-sonnet-4-6",
    maxTokens: endpoint.llmMaxTokens ?? globalDefaults?.maxTokens ?? DEFAULT_MAX_TOKENS,
    customPrompt: endpoint.customPromptInstructions ?? "",
    isDefault: endpoint.isDefaultLlmConfig ?? false,
  })
  const [llmDraft, setLlmDraft] = useState<LlmConfigValue>(llmInitial)

  // Reset local state when selection changes (or when globalDefaults arrives
  // late and the endpoint had no per-setting overrides).
  useEffect(() => {
    setDisplayName(endpoint.displayName)
    setNotes(endpoint.notes ?? "")
    setVmsType(endpoint.vmsType ?? "")
    setDefaultAssignees(endpoint.defaultAssignees ?? [])
    setSkipPublicDomainFilter(endpoint.skipPublicDomainFilter ?? false)
    setLlmDraft(llmInitial())
    onHasChanges(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint.id, endpoint.llmModel, endpoint.llmMaxTokens, endpoint.customPromptInstructions, endpoint.isDefaultLlmConfig, endpoint.skipPublicDomainFilter, globalDefaults])

  const handleCopy = async () => {
    // Use the shared helper (defined at top of this file) so the async
    // Clipboard API failure mode in non-secure Chrome contexts falls back to
    // the textarea/execCommand path instead of silently no-op'ing. We gate
    // the "copied" UI on the helper's boolean return — previously the code
    // fired-and-forgot the writeText Promise, which let the green confirmation
    // flash even when no clipboard write actually happened (the bug the user
    // reported for the VMS Settings Copy button).
    const ok = await copyTextToClipboard(endpoint.endpointEmail)
    if (!ok) return
    setCopied(true)
    // 6s gives the inactive warning enough time to actually be read.
    // The previous 2s vanished before users finished parsing the message.
    setTimeout(() => setCopied(false), 6000)
  }

  const handleSave = async () => {
    const changes: Record<string, unknown> = {}

    // Main info diffs.
    if (displayName !== endpoint.displayName) changes.displayName = displayName
    if (notes !== (endpoint.notes ?? "")) changes.notes = notes || null
    // VmsType: "" tells the backend to clear; any other value updates the pin.
    if (vmsType !== (endpoint.vmsType ?? "")) changes.vmsType = vmsType
    // DefaultAssignees: compare by serialized profileId list to avoid sending a
    // change when the user just opened/closed the picker without edits.
    const prevIds = (endpoint.defaultAssignees ?? []).map(a => a.profileId).join(",")
    const nextIds = defaultAssignees.map(a => a.profileId).join(",")
    if (prevIds !== nextIds) changes.defaultAssignees = defaultAssignees
    // Public-domain bypass — only included in the payload when toggled.
    if (skipPublicDomainFilter !== (endpoint.skipPublicDomainFilter ?? false)) {
      changes.skipPublicDomainFilter = skipPublicDomainFilter
    }

    // LLM diffs — only meaningful for tenant settings; the server drops these
    // on client-level docs but we trim them here for a cleaner payload too.
    if (type === "tenant") {
      const baselineModel = endpoint.llmModel ?? globalDefaults?.model ?? "claude-sonnet-4-6"
      const baselineMax = endpoint.llmMaxTokens ?? globalDefaults?.maxTokens ?? DEFAULT_MAX_TOKENS
      const baselinePrompt = endpoint.customPromptInstructions ?? ""
      const baselineDefault = endpoint.isDefaultLlmConfig ?? false

      if (llmDraft.model !== baselineModel) changes.llmModel = llmDraft.model
      if (llmDraft.maxTokens !== baselineMax) changes.llmMaxTokens = llmDraft.maxTokens
      if (llmDraft.customPrompt !== baselinePrompt) changes.customPromptInstructions = llmDraft.customPrompt.trim() || null
      if (llmDraft.isDefault !== baselineDefault) changes.isDefaultLlmConfig = llmDraft.isDefault
    }

    if (Object.keys(changes).length > 0) {
      await onUpdate(endpoint.id, changes)
      onHasChanges(false)
    }
  }

  const handleDiscard = () => {
    setDisplayName(endpoint.displayName)
    setNotes(endpoint.notes ?? "")
    setVmsType(endpoint.vmsType ?? "")
    setDefaultAssignees(endpoint.defaultAssignees ?? [])
    setSkipPublicDomainFilter(endpoint.skipPublicDomainFilter ?? false)
    setLlmDraft(llmInitial())
    onHasChanges(false)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Unsaved changes bar — only renders when the user can modify. In
          view-only mode hasUnsavedChanges shouldn't go true anyway (inputs
          are disabled), but gating here is belt-and-braces so a stale
          dirty-flag from a previous selection can't surface a Save the
          user couldn't actually use. */}
      {hasUnsavedChanges && canModify && (
        <UnsavedChangesBar onSave={handleSave} onDiscard={handleDiscard} isSaving={isSaving} t={t} />
      )}

      {/* Header with name + status + delete */}
      <div className="px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Circle
              size={8}
              className={`shrink-0 fill-current ${endpoint.isActive ? "text-green-500" : "text-gray-300"}`}
            />
            <h2 className="text-base font-semibold text-gray-800">
              {endpoint.displayName}
            </h2>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
              type === "tenant"
                ? "bg-blue-50 text-blue-600"
                : "bg-amber-50 text-amber-600"
            }`}>
              {type === "tenant" ? t('vmsTenant') : t('client')}
            </span>
            {/* Version badge — surfaces which iteration the user is editing.
                v1 = original; every semantic save increments. Hidden when the
                value is missing/0 to keep legacy docs from showing "v0". */}
            {!!endpoint.versionNumber && endpoint.versionNumber > 0 && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono"
                title={t('vmsVersionTooltip').replace('{n}', String(endpoint.versionNumber))}
              >
                v{endpoint.versionNumber}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {canDelete && (
              <Button
                variant="elliptical-outline"
                size="sm"
                onClick={() => onDelete(endpoint.id)}
                className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 h-7 text-xs"
              >
                <Trash2 size={13} className="mr-1" />
                {t('delete')}
              </Button>
            )}
            <span className="text-xs text-gray-400">
              {endpoint.isActive ? t('active') : t('vmsInactive')}
            </span>
            {/* Active toggle is a write — disable it in view-only mode
                rather than hiding so non-admins can still see whether
                this endpoint is currently accepting email. */}
            <Switch
              checked={endpoint.isActive}
              onCheckedChange={(checked) => onToggleActive(endpoint.id, checked)}
              disabled={!canModify}
            />
          </div>
        </div>
        {/* Endpoint email — the primary identifier for this row. Was a single
            <button> wrapping the email text and the copy icon, which made the
            whole row clickable but had two problems: (1) browsers prevent text
            selection on <button> children, so the user couldn't highlight the
            address with Ctrl+C as a fallback when the Clipboard API failed,
            and (2) clicking inside the button to position a cursor also
            triggered the copy. Split into a <div> wrapper with a selectable
            <code> for the text and a separate inner <button> for the copy
            icon. The whole row still feels like one chip thanks to the shared
            border + hover state (`group` + `group-hover:` on the icon), but
            now the user can either (a) click the icon to copy via the API,
            or (b) drag-select the text and Ctrl+C — both work. */}
        <div
          className="group mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 bg-gray-50 hover:border-gray-300 transition-colors"
        >
          <Mail size={16} className="text-gray-500 shrink-0" />
          {/* `select-all` makes a single click highlight the entire address —
              the most common interaction once users discover the text is
              selectable. `select-text` would still work for click-and-drag
              but requires more precision. `cursor-text` hints at the new
              capability. */}
          <code
            className="text-sm text-gray-700 flex-1 truncate font-mono select-all cursor-text"
            title={endpoint.endpointEmail}
          >
            {endpoint.endpointEmail}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 p-1 rounded hover:bg-gray-200 transition-colors text-gray-400 group-hover:text-gray-600"
            title={t('vmsCopyEmail')}
            aria-label={t('vmsCopyEmail')}
          >
            {copied ? (
              <Check size={16} className="text-green-600" />
            ) : (
              <Copy size={16} />
            )}
          </button>
        </div>
        {copied && (
          <div className="mt-2 text-sm">
            <p className="text-green-700">{t('vmsCopySuccess')}</p>
            {!endpoint.isActive && (
              <p className="text-amber-700 mt-1 flex items-start gap-1.5">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>{t('vmsCopyInactiveWarning')}</span>
              </p>
            )}
          </div>
        )}
        {endpoint.totalEmailsReceived > 0 && (
          <p className="text-sm text-gray-500 mt-2">
            {endpoint.totalEmailsReceived} {t('vmsEmailsReceived')}
            {endpoint.lastEmailReceivedAt && (
              <> · {t('vmsLast')}: {new Date(endpoint.lastEmailReceivedAt).toLocaleDateString()}</>
            )}
          </p>
        )}
      </div>

      {/* Editable fields — Main info + (for tenant settings) LLM Configuration,
          each in its own collapsible. Both write to the same document, so a
          single handleSave at the bottom bundles them into one update call;
          that means one version bump per save instead of two.

          View-only mode (canModify=false) is handled by the <fieldset>
          wrapper: native HTML semantics propagate `disabled` to every form
          control via DOM ancestry, so individual Input / Textarea / Select /
          Switch / Button children stay disabled without needing per-prop
          plumbing. See the layout comment immediately below for why the
          fieldset uses `display: contents`. */}
      {/* Scroll region for the editable sections.
          Structure note (and why the previous shape was wrong):

          OUTER <div>: owns flexbox layout + scroll. `flex-1` claims remaining
          vertical space inside the SettingDetail flex column, `min-h-0` lets
          it shrink below content size so a scrollbar can appear, and
          `overflow-y-auto` provides the scrollbar.

          INNER <fieldset>: owns ONLY the native HTML `disabled` cascade. We
          tried `display: contents` on the fieldset earlier — that's spec-
          compliant but browser support for `contents` on <fieldset> is
          inconsistent (Chromium and Firefox have historically ignored it
          for form-associated elements). When the browser silently fell
          back to block layout, the fieldset's intrinsic
          `min-block-size: min-content` reasserted itself and second+
          collapsible sections couldn't shrink, so the scroll vanished and
          the first section got cut off. Putting the fieldset INSIDE the
          scroll div sidesteps that entirely: the fieldset can be its
          natural block height (= sum of children); the outer div's
          overflow-y-auto takes the scroll responsibility. The fieldset's
          `disabled` attribute still cascades to every Input / Select /
          Switch / Button child through DOM ancestry — that's an HTML
          semantic, independent of CSS layout.

          The `m-0 p-0 border-0 min-w-0` resets neutralise browser-default
          fieldset styling (1px groove border, default padding, intrinsic
          min-width) so the wrapper is visually invisible. */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
      <fieldset disabled={!canModify} className="m-0 p-0 border-0 min-w-0 flex flex-col gap-4 disabled:opacity-95">
        <CollapsibleSection title={t('vmsMainInfo')} icon={<Settings2 size={14} />}>
          {/* Client info (read-only for existing client settings) */}
          {type === "client" && (
            <div>
              <Label className="text-sm text-gray-600">{t('vmsFboClient')}</Label>
              <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                {endpoint.client?.name || endpoint.displayName}
                {endpoint.client?.obCode && (
                  <span className="text-xs text-gray-400 ml-2">({endpoint.client.obCode})</span>
                )}
                {endpoint.client?.fboId && (
                  <span className="text-xs text-gray-400 ml-2">ID: {endpoint.client.fboId}</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('vmsClientReadOnly')}</p>
            </div>
          )}

          <div>
            <Label className="text-sm text-gray-600">{t('vmsDisplayName')}</Label>
            <Input
              value={displayName}
              onChange={(e) => { setDisplayName(e.target.value); onHasChanges(true) }}
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-sm text-gray-600">{t('notes')}</Label>
            <Textarea
              value={notes}
              onChange={(e) => { setNotes(e.target.value); onHasChanges(true) }}
              placeholder={t('vmsNotesPlaceholder')}
              className="mt-1 resize-none"
              rows={3}
            />
          </div>

          <div>
            <Label className="text-sm text-gray-600">{t('vmsTypeLabel')}</Label>
            <Select
              value={vmsType === "" ? "__none__" : vmsType}
              onValueChange={(v) => {
                const next = v === "__none__" ? "" : (v as VmsTypeCode)
                setVmsType(next)
                onHasChanges(true)
              }}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={t('vmsTypeNone')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t('vmsTypeNone')}</SelectItem>
                {VMS_TYPES.map((vt) => (
                  <SelectItem key={vt} value={vt}>{vt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">{t('vmsTypeNote')}</p>
          </div>

          <div>
            <Label className="text-sm text-gray-600">{t('vmsDefaultAssignees')}</Label>
            <div className="mt-1">
              <DefaultAssigneesEditor
                value={defaultAssignees}
                onChange={(next) => { setDefaultAssignees(next); onHasChanges(true) }}
                searchUsers={searchUsers}
                t={t}
              />
            </div>
          </div>

          {/* Public-domain bypass — gates whether emails from public mail
              providers (gmail / outlook / yahoo / ~6k disposable providers)
              auto-create a VmsRequest, or get parked at PendingManualCreate
              for a reviewer to action.

              Tenant-only by design. The processor reads this via the linked
              LLM-config resolver chain (ResolveLlmConfigSettingAsync), so the
              tenant value cascades to every client that inherits from it —
              setting it once per tenant covers all linked clients without
              per-row management. Mirrors how LlmModel / MaxTokens / custom
              prompt are tenant-only with client inheritance.

              Off by default — that's the safe choice (avoids creating
              requests from forwarded personal mail by mistake). */}
          {type === "tenant" && (
            <div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Label className="text-sm text-gray-600">
                    {t('vmsSkipPublicDomainFilterLabel')}
                  </Label>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('vmsSkipPublicDomainFilterNote')}
                  </p>
                </div>
                <Switch
                  checked={skipPublicDomainFilter}
                  onCheckedChange={(checked) => { setSkipPublicDomainFilter(checked); onHasChanges(true) }}
                  aria-label={t('vmsSkipPublicDomainFilterLabel')}
                />
              </div>
            </div>
          )}
        </CollapsibleSection>

        {/* LLM Configuration — tenant settings only. Client settings inherit
            via LinkedTenantSettingsId, so editing LLM fields on a client doc
            would be confusing (the server drops them too). */}
        {type === "tenant" && (
          <CollapsibleSection title={t('vmsLlmConfig')} icon={<Bot size={14} />}>
            <LlmConfigFields
              value={llmDraft}
              onChange={(next) => { setLlmDraft(next); onHasChanges(true) }}
              globalDefaults={globalDefaults}
              emailDomain={emailDomain}
              showEmailDomain
              t={t}
            />
          </CollapsibleSection>
        )}

        {/* History — lazy-loaded audit timeline. Collapsed by default so the
            initial render of an endpoint detail doesn't fire an extra request
            for users who don't care about history. Requires the doc to have a
            settingsGroupId (legacy pre-versioning docs won't show this). */}
        {endpoint.settingsGroupId && (
          <CollapsibleSection
            title={t('vmsHistorySection')}
            icon={<History size={14} />}
            defaultOpen={false}
          >
            <AuditHistorySection
              settingsGroupId={endpoint.settingsGroupId}
              fetchHistory={fetchAuditHistory}
              t={t}
            />
          </CollapsibleSection>
        )}

        {/* Single inline save covers BOTH editable sections. The top
            unsaved-changes bar uses the same handleSave. History is read-only.
            Hidden in view-only mode — fieldset would disable the click but a
            visible-yet-disabled save button reads as "you almost have access"
            which is worse than hiding the affordance entirely. */}
        {hasUnsavedChanges && canModify && (
          <Button
            variant="elliptical"
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? t('atsSyncSaving') : t('saveChanges')}
          </Button>
        )}
      </fieldset>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
//  CREATE FORM — shown in right panel when adding new
// ════════════════════════════════════════════════════════════

function CreateSettingForm({
  type,
  onCreate,
  isCreating,
  onCancel,
  searchClientOrgs,
  searchUsers,
  globalDefaults,
  t,
}: {
  type: VmsSettingType
  onCreate: (dto: CreateVmsSettingsDto) => Promise<void>
  isCreating: boolean
  onCancel: () => void
  searchClientOrgs: (query: string) => Promise<{ entityId: number; displayName: string; organizationCode: string }[]>
  searchUsers: (q: string) => Promise<UserReportDto[]>
  globalDefaults: PromptConfigDto | null
  t: (key: string) => string
}) {
  const [displayName, setDisplayName] = useState("")
  const [notes, setNotes] = useState("")
  const [vmsType, setVmsType] = useState<VmsTypeCode | "">("")
  const [defaultAssignees, setDefaultAssignees] = useState<VmsUserReportDto[]>([])
  // Public-domain bypass — see VmsEndpointDto.skipPublicDomainFilter. Defaults
  // to false (the safe choice); operator opts an endpoint in when they
  // legitimately want to accept gmail/outlook/etc. senders without a human
  // review step.
  const [skipPublicDomainFilter, setSkipPublicDomainFilter] = useState<boolean>(false)
  // LLM draft state for tenant create — seeded from global defaults so the user
  // sees what they'd inherit by leaving the fields alone. Client create ignores
  // these (server drops them on the floor for client-level settings).
  const [llmDraft, setLlmDraft] = useState<LlmConfigValue>(() => ({
    model: globalDefaults?.model ?? "claude-sonnet-4-6",
    maxTokens: globalDefaults?.maxTokens ?? DEFAULT_MAX_TOKENS,
    customPrompt: "",
    isDefault: false,
  }))
  useEffect(() => {
    // Re-seed if globalDefaults loads after this form mounts (typical async race
    // on a fresh page open). Only touch fields that are still at their default
    // values to avoid clobbering user edits.
    setLlmDraft((prev) => ({
      ...prev,
      model: prev.model || (globalDefaults?.model ?? "claude-sonnet-4-6"),
      maxTokens: prev.maxTokens || (globalDefaults?.maxTokens ?? DEFAULT_MAX_TOKENS),
    }))
  }, [globalDefaults])
  const [selectedClient, setSelectedClient] = useState<VmsClientDto | null>(null)
  const [clientSearchQuery, setClientSearchQuery] = useState("")
  const [clientSearchResults, setClientSearchResults] = useState<{ entityId: number; displayName: string; organizationCode: string }[]>([])
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [isSearching, setIsSearching] = useState(false)

  // Debounced client search
  const clientSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastClientSearchRef = useRef("")
  const searchClientOrgsRef = useRef(searchClientOrgs)
  searchClientOrgsRef.current = searchClientOrgs

  useEffect(() => {
    return () => { if (clientSearchTimeoutRef.current) clearTimeout(clientSearchTimeoutRef.current) }
  }, [])

  const handleClientSearch = (query: string) => {
    setClientSearchQuery(query)
    if (clientSearchTimeoutRef.current) clearTimeout(clientSearchTimeoutRef.current)
    if (query.length < 2) {
      setClientSearchResults([])
      setShowClientDropdown(false)
      return
    }
    clientSearchTimeoutRef.current = setTimeout(async () => {
      if (query === lastClientSearchRef.current) return
      lastClientSearchRef.current = query
      setIsSearching(true)
      try {
        const results = await searchClientOrgsRef.current(query)
        setClientSearchResults(results)
        setShowClientDropdown(results.length > 0)
      } catch {
        setClientSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 600)
  }

  const handleSelectClient = (client: { entityId: number; displayName: string; organizationCode: string }) => {
    setSelectedClient({
      fboId: client.entityId,
      name: client.displayName,
      obCode: null,
    })
    setShowClientDropdown(false)
    setClientSearchQuery("")
    lastClientSearchRef.current = ""
    // Auto-populate display name if empty
    if (!displayName.trim()) {
      setDisplayName(`${client.displayName} VMS`)
    }
  }

  const handleClearClient = () => {
    setSelectedClient(null)
    setClientSearchQuery("")
    lastClientSearchRef.current = ""
  }

  const handleSubmit = async () => {
    if (!displayName.trim()) return
    // LLM fields are only meaningful at the tenant level. Server-side guards
    // also enforce this, but trimming them client-side keeps the create payload
    // honest and prevents a "client setting has LLM overrides" misimpression in
    // any future audit.
    const llmPayload = type === "tenant"
      ? {
          llmModel: llmDraft.model,
          llmMaxTokens: llmDraft.maxTokens,
          customPromptInstructions: llmDraft.customPrompt.trim() || null,
          isDefaultLlmConfig: llmDraft.isDefault,
        }
      : {}
    await onCreate({
      displayName: displayName.trim(),
      client: type === "client" ? selectedClient : null,
      notes: notes.trim() || null,
      vmsType: vmsType === "" ? null : vmsType,
      defaultAssignees,
      skipPublicDomainFilter,
      ...llmPayload,
    })
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
        <h2 className="text-base font-semibold text-gray-800">
          {type === "tenant" ? t('vmsNewTenantSetting') : t('vmsNewClientSetting')}
        </h2>
        <p className="text-xs text-gray-400 mt-1">
          {type === "tenant"
            ? t('vmsTenantDescription')
            : t('vmsClientDescription')}
        </p>
      </div>

      {/* Same scroll-region treatment as the detail view's scroll wrapper —
          min-h-0 lets flex-1 shrink below content size so the scrollbar
          actually appears, and gap-4 on the flex column replaces space-y-4
          (consistent with SettingDetail and avoids margin/border conflicts
          between adjacent CollapsibleSection borders). */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-4">
        <CollapsibleSection title={t('vmsMainInfo')} icon={<Settings2 size={14} />}>
          {/* Client search FIRST for client type */}
          {type === "client" && (
            <div className="relative">
              <Label className="text-sm text-gray-600">{t('vmsFboClient')}</Label>
              {selectedClient ? (
                <div className="mt-1 flex items-center gap-2">
                  <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                    {selectedClient.name}
                    {selectedClient.obCode && (
                      <span className="text-xs text-gray-400 ml-2">({selectedClient.obCode})</span>
                    )}
                    <span className="text-xs text-gray-400 ml-2">ID: {selectedClient.fboId}</span>
                  </div>
                  <button
                    onClick={handleClearClient}
                    className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600"
                    title={t('vmsChangeClient')}
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <Input
                    value={clientSearchQuery}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    placeholder={t('vmsSearchClient')}
                    className="mt-1"
                  />
                  {isSearching && (
                    <p className="text-xs text-gray-500 mt-1">{t('searchingUsers')}</p>
                  )}
                  {showClientDropdown && (
                    <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {clientSearchResults.map((c) => (
                        <button
                          key={c.entityId}
                          onClick={() => handleSelectClient(c)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <span className="font-medium text-gray-800">{c.displayName}</span>
                          {c.organizationCode && (
                            <span className="text-xs text-gray-400 ml-2">({c.organizationCode})</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <Label className="text-sm text-gray-600">{t('vmsDisplayName')}</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={type === "tenant" ? t('vmsTenantNameExample') : t('vmsClientNameExample')}
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-sm text-gray-600">{t('vmsNotesOptional')}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('vmsNotesPlaceholder')}
              className="mt-1 resize-none"
              rows={3}
            />
          </div>

          <div>
            <Label className="text-sm text-gray-600">{t('vmsTypeLabel')}</Label>
            <Select
              value={vmsType === "" ? "__none__" : vmsType}
              onValueChange={(v) => setVmsType(v === "__none__" ? "" : (v as VmsTypeCode))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={t('vmsTypeNone')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t('vmsTypeNone')}</SelectItem>
                {VMS_TYPES.map((vt) => (
                  <SelectItem key={vt} value={vt}>{vt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">{t('vmsTypeNote')}</p>
          </div>

          <div>
            <Label className="text-sm text-gray-600">{t('vmsDefaultAssignees')}</Label>
            <div className="mt-1">
              <DefaultAssigneesEditor
                value={defaultAssignees}
                onChange={setDefaultAssignees}
                searchUsers={searchUsers}
                t={t}
              />
            </div>
          </div>

          {/* Public-domain bypass — tenant-only (see matching control on
              SettingDetail for the long-form explanation). Client settings
              inherit through the LLM-config resolver chain so a per-client
              toggle would just add management noise — the tenant flag
              cascades automatically. */}
          {type === "tenant" && (
            <div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Label className="text-sm text-gray-600">
                    {t('vmsSkipPublicDomainFilterLabel')}
                  </Label>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('vmsSkipPublicDomainFilterNote')}
                  </p>
                </div>
                <Switch
                  checked={skipPublicDomainFilter}
                  onCheckedChange={setSkipPublicDomainFilter}
                  aria-label={t('vmsSkipPublicDomainFilterLabel')}
                />
              </div>
            </div>
          )}
        </CollapsibleSection>

        {/* LLM configuration — tenant only, visible upfront so the operator can
            set model/tokens/prompt in the same flow as create. Client settings
            inherit from a linked tenant config so this section doesn't apply. */}
        {type === "tenant" && (
          <CollapsibleSection title={t('vmsLlmConfig')} icon={<Bot size={14} />}>
            <LlmConfigFields
              value={llmDraft}
              onChange={setLlmDraft}
              globalDefaults={globalDefaults}
              emailDomain={null}
              showEmailDomain={false}
              t={t}
            />
          </CollapsibleSection>
        )}
      </div>

      <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex gap-2">
        <Button
          variant="elliptical"
          size="sm"
          onClick={handleSubmit}
          disabled={!displayName.trim() || isCreating || (type === "client" && !selectedClient)}
        >
          {isCreating ? t('atsCreatingButton') : t('atsCreateButton')}
        </Button>
        <Button variant="elliptical-outline" size="sm" onClick={onCancel}>
          {t('cancel')}
        </Button>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
//  LLM CONFIG FIELDS — controlled form rows, used by both edit
//  and create flows. Pure presentation; parent owns save logic.
// ════════════════════════════════════════════════════════════

/** Shape held by LlmConfigFields' parent. */
interface LlmConfigValue {
  model: string
  maxTokens: number
  customPrompt: string
  isDefault: boolean
}

/**
 * Renders the LLM config form rows (model, max tokens, custom prompt, default
 * flag) against a controlled value. Used in both edit mode (LlmConfigSection
 * wraps it with a save button) and create mode (the create form bundles these
 * values into the create payload). No internal state — parent fully owns it.
 */
function LlmConfigFields({
  value,
  onChange,
  globalDefaults,
  emailDomain,
  showEmailDomain,
  t,
}: {
  value: LlmConfigValue
  onChange: (next: LlmConfigValue) => void
  globalDefaults: PromptConfigDto | null
  emailDomain: string | null
  /** Whether to render the email-domain footer. Edit mode shows it; create hides it (no email yet). */
  showEmailDomain: boolean
  t: (key: string) => string
}) {
  const handleMaxTokensChange = (raw: string) => {
    const parsed = parseInt(raw, 10)
    if (!isNaN(parsed) && parsed > 0) {
      onChange({ ...value, maxTokens: Math.min(parsed, MAX_TOKENS_CAP) })
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-400">{t('vmsLlmConfigDescription')}</p>
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div>
          <Label className="text-sm text-gray-600">{t('vmsModel')}</Label>
          <Select value={value.model} onValueChange={(v) => onChange({ ...value, model: v })}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALLOWED_LLM_MODELS.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm text-gray-600">{t('vmsMaxTokens')}</Label>
          <Input
            type="number"
            value={value.maxTokens}
            onChange={(e) => handleMaxTokensChange(e.target.value)}
            className="mt-1"
            min={256}
            max={MAX_TOKENS_CAP}
          />
          <p className="text-[10px] text-gray-400 mt-0.5">Max: {MAX_TOKENS_CAP.toLocaleString()}</p>
        </div>
      </div>

      <div>
        <Label className="text-sm text-gray-600">{t('vmsCustomPrompt')}</Label>
        <Textarea
          value={value.customPrompt}
          onChange={(e) => onChange({ ...value, customPrompt: e.target.value })}
          placeholder={t('vmsCustomPromptPlaceholder')}
          className="mt-1 resize-none text-xs font-mono"
          rows={4}
        />
        <p className="text-[10px] text-gray-400 mt-0.5">{t('vmsPromptSafetyNote')}</p>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="isDefaultLlmConfig"
          checked={value.isDefault}
          onChange={(e) => onChange({ ...value, isDefault: e.target.checked })}
          className="rounded border-gray-300"
        />
        <Label htmlFor="isDefaultLlmConfig" className="text-xs text-gray-600 cursor-pointer">
          {t('vmsDefaultLlmConfig')}
        </Label>
      </div>

      {globalDefaults && (
        <p className="text-xs text-gray-400">
          Provider: {globalDefaults.provider}
          {globalDefaults.anthropicApiVersion && ` · API version: ${globalDefaults.anthropicApiVersion}`}
        </p>
      )}

      {showEmailDomain && emailDomain && (
        <div className="pt-3 mt-3 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <Mail size={13} className="text-gray-400" />
            <span className="text-xs text-gray-500">
              {t('vmsEmailDomain')} <code className="font-mono text-gray-700">{emailDomain}</code>
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">{t('vmsEmailDomainNote')}</p>
        </div>
      )}
    </div>
  )
}

// (LlmConfigSection wrapper removed — LLM Configuration now renders as
//  LlmConfigFields directly inside SettingDetail's flex body so the save
//  flow is unified with Main info. Form draft state and dirty tracking
//  live on SettingDetail.)

// ════════════════════════════════════════════════════════════
//  MAIN FULL-SCREEN PANEL
// ════════════════════════════════════════════════════════════

interface VmsSettingsPanelProps {
  onClose: () => void
}

export function VmsSettingsPanel({ onClose }: VmsSettingsPanelProps) {
  const { t } = useTranslation()
  const vmsApi = useVmsApi()
  const { profileId } = useAuth()

  // Auth source: the VmsRequest configuration specifically. The panel is
  // rendered above the VMS sub-tab WorkflowProviders (it lives in
  // vms/tab.tsx, outside VmsSubTabContent), so useWorkflow() walks up to
  // the workflow-hub-level WorkOrder provider — whose configuration
  // doesn't carry VMS-specific codes. We read from VmsRequest because:
  //   - The backend controller for these endpoints
  //     (POST/PUT/DELETE /api/vms/config/endpoints) authorizes against
  //     EntityTypes.VmsRequest. Matching that here keeps frontend ↔
  //     backend in lockstep.
  //   - Unioning multiple configs (an earlier draft did this) leaked
  //     write codes between entity types: ModifyVmsSettings granted only
  //     on VmsEmail would surface Client write affordances in the
  //     settings panel, which would then 401 at the backend (which
  //     checks VmsRequest). Single-source-of-truth fixes that.
  // Falls back to an empty array while useVmsConfig is still loading so
  // the panel renders read-only during the brief mount window rather
  // than flashing buttons that disappear once auth resolves.
  const { subTabs: vmsSubTabs } = useVmsConfig()
  const authorizedActions = useMemo(() => {
    const vmsRequestSubTab = vmsSubTabs.find(
      (t: any) => t.entityType === "VmsRequest"
    )
    return ((vmsRequestSubTab?.configuration as any)?.authorizedActions ?? []).map(
      (a: any) => (typeof a === "string" ? a : a.actionCode)
    ) as string[]
  }, [vmsSubTabs])
  /**
   * Two-tier write gating with STRICT type separation — each code gates
   * only its own settings type, with no implicit subset relationship:
   *
   *  - <c>ModifyVmsSettings</c> → Client settings write only.
   *  - <c>ModifyVmsTenantSettings</c> → Tenant settings write only.
   *
   * Users granted ONLY the Tenant code can still manage Tenant docs but
   * NOT Client docs — and vice versa. This matches the user's stated
   * mental model: the two grants are independent capabilities and we
   * shouldn't sneak Tenant admins into Client writes just because the
   * Tenant code "sounds bigger". Users with neither code see the panel
   * in pure view-only mode (gated separately on <c>ViewVmsSettings</c>).
   */
  const canModifyClient = authorizedActions.includes("ModifyVmsSettings")
  const canModifyTenant = authorizedActions.includes("ModifyVmsTenantSettings")

  /**
   * Per-endpoint modify capability. Type discriminator: <c>endpoint.client
   * == null</c> → Tenant doc. The required code is determined STRICTLY by
   * the doc's type — no fallback to the other code.
   */
  const canModifyEndpoint = useCallback(
    (ep: VmsEndpointDto) => {
      const isTenantSetting = ep.client == null
      return isTenantSetting ? canModifyTenant : canModifyClient
    },
    [canModifyClient, canModifyTenant]
  )

  /** Delete follows the same strict per-type gate as edit. */
  const canDeleteEndpoint = canModifyEndpoint

  const [endpoints, setEndpoints] = useState<VmsEndpointDto[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loadedPage, setLoadedPage] = useState(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const PAGE_SIZE = 50
  const [globalDefaults, setGlobalDefaults] = useState<PromptConfigDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [emailDomain, setEmailDomain] = useState<string | null>(null)

  // List state
  const [searchText, setSearchText] = useState("")
  const [typeFilter, setTypeFilter] = useState<"all" | VmsSettingType>("all")
  const [showActiveOnly, setShowActiveOnly] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Create mode
  const [createMode, setCreateMode] = useState<VmsSettingType | null>(null)

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<{ type: "select"; id: string } | { type: "create"; settingType: VmsSettingType } | null>(null)

  // Delete confirmation
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)

  // Client organization search — uses existing FBO report API (report/34).
  // User search reuses the same endpoint as the rest of workflow-hub-v2 so the
  // assignee picker matches the AssignedToUser picker on requests.
  const { searchClientOrganizations, searchUsers } = useWorkflowApi()

  // Filtered list
  const filteredEndpoints = useMemo(() => {
    let items = endpoints
    if (typeFilter !== "all") {
      items = items.filter((ep) => getSettingType(ep) === typeFilter)
    }
    if (showActiveOnly) {
      items = items.filter((ep) => ep.isActive)
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase()
      items = items.filter(
        (ep) =>
          ep.displayName.toLowerCase().includes(q) ||
          (ep.client?.name && ep.client.name.toLowerCase().includes(q)) ||
          ep.endpointEmail.toLowerCase().includes(q)
      )
    }
    return items
  }, [endpoints, typeFilter, showActiveOnly, searchText])

  const selectedEndpoint = endpoints.find((ep) => ep.id === selectedId) ?? null
  const selectedType = selectedEndpoint ? getSettingType(selectedEndpoint) : null

  // (LLM config visibility is now decided locally inside SettingDetail and
  // CreateSettingForm — both check `type === "tenant"` before rendering the
  // LLM section. No parent-level showLlmConfig flag needed.)

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const [page, defaults, domain] = await Promise.all([
        vmsApi.getEndpoints(1, PAGE_SIZE),
        vmsApi.getPromptConfig(),
        vmsApi.getEmailDomain(),
      ])
      setEndpoints(page.items)
      setTotalCount(page.totalCount)
      setLoadedPage(1)
      setGlobalDefaults(defaults)
      setEmailDomain(domain)
      // Auto-select first item on this page (if no selection persists)
      if (page.items.length > 0 && !selectedId) {
        setSelectedId(page.items[0].id)
      }
    } finally {
      setIsLoading(false)
    }
  }, [vmsApi])

  /**
   * Loads the next page of endpoints and appends them to the list. The filter
   * UI (search / type / active-only) operates client-side on the loaded items,
   * so loading more makes those filters see more data. Stops being available
   * once endpoints.length >= totalCount.
   */
  const loadMore = useCallback(async () => {
    if (isLoadingMore) return
    setIsLoadingMore(true)
    try {
      const next = loadedPage + 1
      const page = await vmsApi.getEndpoints(next, PAGE_SIZE)
      setEndpoints((prev) => {
        // De-dup defensively: if the user happened to create/edit a setting
        // while a "load more" was in flight, the same id could appear on two
        // pages. Keep the newest (incoming) copy in that case.
        const seen = new Map(prev.map((ep) => [ep.id, ep]))
        for (const ep of page.items) seen.set(ep.id, ep)
        return Array.from(seen.values())
      })
      setTotalCount(page.totalCount)
      setLoadedPage(next)
    } finally {
      setIsLoadingMore(false)
    }
  }, [vmsApi, loadedPage, isLoadingMore])

  // Strict-mode-safe one-shot loader. next.config.mjs enables
  // `reactStrictMode: true`, which intentionally double-invokes effects in
  // development (mount → unmount → remount) to surface ordering bugs. Without
  // this ref guard the first mount fires `loadData`, then StrictMode tears
  // down and remounts the component which fires `loadData` again — visible in
  // the Network tab as duplicate `endpoints?pageNumber=...`, `prompt`, and
  // `domain` requests on settings-panel open. Production builds don't run
  // StrictMode so this is purely a dev-noise fix, but it makes the dev
  // network tab actually useful for debugging real duplicates (e.g. the
  // useVmsApi memo loop we just fixed). Same pattern as request-progress-panel.
  const didLoadRef = useRef(false)
  useEffect(() => {
    if (didLoadRef.current) return
    didLoadRef.current = true
    loadData()
  }, [loadData])

  // Escape to close — respects the same unsaved-changes guard the in-panel
  // navigation uses (handleSelect / handleAdd). The pendingNavigation state
  // machine doesn't have a "close" type, so for the dismissal path we fall
  // back to a confirm prompt. If the user OKs, we proceed; otherwise the
  // panel stays open and their edits are preserved.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      e.preventDefault()
      if (hasUnsavedChanges) {
        const ok = window.confirm("You have unsaved changes. Discard and close?")
        if (!ok) return
      }
      onClose()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose, hasUnsavedChanges])

  // Navigation with unsaved changes guard
  const handleSelect = (id: string) => {
    if (id === selectedId) return
    if (hasUnsavedChanges) {
      setPendingNavigation({ type: "select", id })
      return
    }
    setSelectedId(id)
    setCreateMode(null)
  }

  const handleAdd = (type: VmsSettingType) => {
    if (hasUnsavedChanges) {
      setPendingNavigation({ type: "create", settingType: type })
      return
    }
    setCreateMode(type)
    setSelectedId(null)
  }

  const confirmNavigation = () => {
    setHasUnsavedChanges(false)
    if (pendingNavigation?.type === "select") {
      setSelectedId(pendingNavigation.id)
      setCreateMode(null)
    } else if (pendingNavigation?.type === "create") {
      setCreateMode(pendingNavigation.settingType)
      setSelectedId(null)
    }
    setPendingNavigation(null)
  }

  const cancelNavigation = () => {
    setPendingNavigation(null)
  }

  const handleCreate = async (dto: CreateVmsSettingsDto) => {
    setIsCreating(true)
    try {
      const created = await vmsApi.createEndpoint(dto)
      if (created) {
        setEndpoints((prev) => [...prev, created])
        // Keep the "Showing X of Y" hint truthful — a new endpoint adds one
        // both to the local list and the server total.
        setTotalCount((prev) => prev + 1)
        setSelectedId(created.id)
        setCreateMode(null)
      }
    } finally {
      setIsCreating(false)
    }
  }

  const handleToggleActive = async (id: string, isActive: boolean) => {
    const updated = await vmsApi.updateEndpoint(id, { isActive })
    if (updated) {
      setEndpoints((prev) =>
        prev.map((ep) => (ep.id === id ? { ...ep, ...updated } : ep))
      )
    }
  }

  const handleUpdate = async (id: string, changes: Record<string, unknown>) => {
    setIsSaving(true)
    try {
      const updated = await vmsApi.updateEndpoint(id, changes as any)
      if (updated) {
        // Semantic edits create a NEW version doc with a NEW id. We replace the
        // old row in our local list with the new doc, then re-point selectedId
        // at it so the detail pane keeps showing the same setting instead of
        // emptying out. Cosmetic edits mutate in place — updated.id === id —
        // so the reselection is a no-op in that case.
        setEndpoints((prev) =>
          prev.map((ep) => (ep.id === id ? { ...ep, ...updated } : ep))
        )
        if (selectedId === id && updated.id !== id) {
          setSelectedId(updated.id)
        }
        // If this save flipped IsDefaultLlmConfig on, mirror the "only one
        // default per tenant" rule client-side so the user doesn't see two
        // green-dot defaults for an instant before a refresh.
        if (updated.isDefaultLlmConfig && !updated.client) {
          setEndpoints((prev) =>
            prev.map((ep) => (
              ep.id !== updated.id && !ep.client && ep.isDefaultLlmConfig
                ? { ...ep, isDefaultLlmConfig: false }
                : ep
            ))
          )
        }
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    setDeleteTargetId(id)
  }

  const confirmDelete = async () => {
    if (!deleteTargetId) return
    setIsDeleting(true)
    try {
      const success = await vmsApi.deleteEndpoint(deleteTargetId)
      if (success) {
        setEndpoints((prev) => prev.filter((ep) => ep.id !== deleteTargetId))
        // Decrement so the pagination hint stays accurate after a soft-delete.
        setTotalCount((prev) => Math.max(0, prev - 1))
        if (selectedId === deleteTargetId) {
          setSelectedId(null)
        }
      }
    } finally {
      setIsDeleting(false)
      setDeleteTargetId(null)
    }
  }

  // (handleSaveLlmConfig deleted — LLM fields now save through handleUpdate
  // alongside Main info. Single update call → single version bump.)

  const deleteTarget = deleteTargetId ? endpoints.find((ep) => ep.id === deleteTargetId) : null

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <DeleteConfirmDialog
          settingName={deleteTarget.displayName}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTargetId(null)}
          isDeleting={isDeleting}
          t={t}
        />
      )}

      {/* Pending navigation confirmation dialog */}
      {pendingNavigation && (
        <div className="fixed inset-0 z-[60] bg-black/30 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-amber-500" />
              <h3 className="font-semibold text-gray-800">{t('unsavedChangesTitle')}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {t('vmsDiscardConfirm')}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="elliptical-outline" size="sm" onClick={cancelNavigation}>
                {t('vmsGoBack')}
              </Button>
              <Button variant="elliptical" size="sm" onClick={confirmNavigation}>
                {t('vmsDiscardContinue')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-gray-200 bg-[#F9F9FF] flex-shrink-0">
        <div className="flex items-center gap-3">
          <Settings2 size={22} className="text-[#0F214B]" />
          <h1 className="text-xl font-semibold text-[#0F214B]">
            {t('vmsSettingsTitle')}
          </h1>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
          <X size={20} />
        </Button>
      </div>

      {/* ── Body: split layout ── */}
      <div className="flex-1 flex overflow-hidden">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
          </div>
        ) : (
          <>
            {/* Left: settings list */}
            <div className="w-[480px] shrink-0">
              <SettingsList
                items={filteredEndpoints}
                selectedId={selectedId}
                onSelect={handleSelect}
                onAdd={handleAdd}
                onDelete={handleDelete}
                canDelete={canDeleteEndpoint}
                canCreateTenant={canModifyTenant}
                canCreateClient={canModifyClient}
                searchText={searchText}
                onSearchChange={setSearchText}
                typeFilter={typeFilter}
                onTypeFilterChange={setTypeFilter}
                showActiveOnly={showActiveOnly}
                onShowActiveOnlyChange={setShowActiveOnly}
                totalCount={totalCount}
                loadedCount={endpoints.length}
                onLoadMore={loadMore}
                isLoadingMore={isLoadingMore}
                t={t}
              />
            </div>

            {/* Right: detail/create (top) + LLM config (bottom, tenant only) */}
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
              {/* Detail / Create area — always on top */}
              {/* Detail / Create occupies the full right column now. LLM
                  config used to live in a separate flex section below; it's
                  been inlined into both SettingDetail (edit) and
                  CreateSettingForm (create) so save flows are unified.

                  overflow-hidden (NOT overflow-auto) and min-h-0 are both
                  load-bearing here: SettingDetail / CreateSettingForm own
                  their own scroll regions via an inner fieldset/wrapper
                  with overflow-y-auto. Without overflow-hidden + min-h-0
                  on THIS container, the outer wrapper grows to fit its
                  content and steals the scroll, which pushed the panel
                  title up out of view and caused sections to overlap as
                  the inner flex children expanded unconstrained. */}
              <div className="flex-1 overflow-hidden min-h-0">
                {/* Defensive gate on the create form: handleAdd is the only
                    UI entry point and its triggers are already hidden for
                    users lacking the matching code. Re-check here against
                    the SPECIFIC createMode so the form for a type the user
                    can't actually submit (would 401 on the backend) never
                    renders even on programmatic state changes. */}
                {createMode && (
                  createMode === "tenant" ? canModifyTenant : canModifyClient
                ) ? (
                  <CreateSettingForm
                    type={createMode}
                    onCreate={handleCreate}
                    isCreating={isCreating}
                    onCancel={() => {
                      setCreateMode(null)
                      if (endpoints.length > 0) setSelectedId(endpoints[0].id)
                    }}
                    searchClientOrgs={searchClientOrganizations}
                    searchUsers={searchUsers}
                    globalDefaults={globalDefaults}
                    t={t}
                  />
                ) : selectedEndpoint ? (
                  <SettingDetail
                    endpoint={selectedEndpoint}
                    onToggleActive={handleToggleActive}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    isSaving={isSaving}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onHasChanges={setHasUnsavedChanges}
                    canDelete={canDeleteEndpoint(selectedEndpoint)}
                    canModify={canModifyEndpoint(selectedEndpoint)}
                    searchUsers={searchUsers}
                    fetchAuditHistory={vmsApi.getEndpointAuditHistory}
                    globalDefaults={globalDefaults}
                    emailDomain={emailDomain}
                    t={t}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full min-h-[200px] text-sm text-gray-400">
                    {t('vmsSelectSetting')}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
